import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, FormControlLabel, Switch,
  Box, Typography, Alert, CircularProgress,
  IconButton, InputAdornment,
} from '@mui/material';
import { ContentCopy as CopyIcon, OpenInNew as OpenIcon } from '@mui/icons-material';
import { shareBikeAnalysis } from '../api/bikeShareApi';

export default function ShareBikeAnalysisDialog({ open, onClose, analysisSessionId }) {
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await shareBikeAnalysis(analysisSessionId, {
        displayName: displayName || null,
        description: description || null,
        isAnonymous,
      });
      setShareUrl(`${window.location.origin}${result.share_url}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create share link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setDisplayName('');
    setDescription('');
    setIsAnonymous(false);
    setShareUrl(null);
    setError(null);
    setCopied(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Share Bike Analysis</DialogTitle>
      <DialogContent>
        {!shareUrl ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Display Name (optional)"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              fullWidth
              placeholder="Epic Gran Fondo"
              helperText="Public name for this analysis"
            />
            <TextField
              label="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={3}
              fullWidth
              placeholder="Sunday morning ride with the club..."
            />
            <FormControlLabel
              control={<Switch checked={isAnonymous} onChange={(e) => setIsAnonymous(e.target.checked)} />}
              label="Share anonymously (hide my name)"
            />
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Alert severity="success">Share link created successfully!</Alert>
            <TextField
              label="Shareable Link"
              value={shareUrl}
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCopy} edge="end" title="Copy link">
                      <CopyIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {copied && (
              <Typography variant="body2" color="success.main">Link copied to clipboard!</Typography>
            )}
            <Button
              variant="outlined"
              startIcon={<OpenIcon />}
              onClick={() => window.open(shareUrl, '_blank')}
              fullWidth
            >
              View Shared Page
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!shareUrl ? (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleShare}
              disabled={loading || !analysisSessionId}
              startIcon={loading && <CircularProgress size={16} />}
            >
              {loading ? 'Creating...' : 'Create Share Link'}
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={handleClose}>Done</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
